import styled, { css } from 'styled-components';

import { LocationText } from 'components/SharedFormComponents/styles';

// The gap and bottom padding are the answer's only vertical space: AnswerRow is horizontal-only.
export const QuestionCard = styled.div<{ $open?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 20px 20px 16px;
  padding-bottom: 20px;
  background: ${({ theme }) => theme.palette.common.white};
  border: 1px solid ${({ theme, $open }) => ($open ? theme.palette.primary.main : theme.palette.divider)};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${({ theme, $open }) => ($open ? `0 4px 50px ${theme.palette.grey[500]}` : 'none')};
`;

export const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 20px;
  background: ${({ theme }) => theme.palette.grey[100]};
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const QuestionLabel = styled.p`
  margin: 0;
  font-size: 18px;
  color: ${({ theme }) => theme.palette.primary.dark};
`;

export const Mandatory = styled.span`
  margin-left: 6px;
  color: ${({ theme }) => theme.ui.alarm.main};
`;

// Image dimensions aren't on the payload, so the box is reserved up front: an auto-height <img> grows from
// nothing to ~400px as it loads and shoves the rest of the record down.
const IMAGE_BOX_HEIGHT = '280px';

export const QuestionImageRow = styled.div`
  padding: 0 20px;
`;

// scale-down shrinks a large photo to fit but never stretches a small one.
export const QuestionImage = styled.img`
  display: block;
  width: 100%;
  height: ${IMAGE_BOX_HEIGHT};
  object-fit: scale-down;
  border-radius: 4px;
`;

export const ImageUnavailable = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${IMAGE_BOX_HEIGHT};
  font-size: 18px;
  color: ${({ theme }) => theme.palette.text.disabled};
  background: ${({ theme }) => theme.palette.grey[100]};
  border: 1px dashed ${({ theme }) => theme.palette.divider};
  border-radius: 4px;
`;

export const AttachmentAnswer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const AttachmentThumbs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

// A fixed tile like the feed's 200px thumbnails, so the grid doesn't reflow as photos load.
export const ThumbLink = styled.a`
  display: block;
  width: 200px;
  height: 150px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 4px;
`;

export const ThumbImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const ThumbFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  padding: 8px;
  color: ${({ theme }) => theme.palette.text.secondary};
  background: ${({ theme }) => theme.palette.grey[50]};
`;

export const ThumbFilename = styled.span`
  max-width: 100%;
  overflow: hidden;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const AnswerRow = styled.div`
  padding: 0 20px;
  background: ${({ theme }) => theme.palette.common.white};
`;

/**
 * Text answers read at 18px like the legacy record; chips, links and rows keep their own size.
 * `$withIcon` is for a location, which CustomFieldDisplayValue returns as a bare pin and <p>, leaving the row to us.
 */
export const AnswerWrapper = styled.div<{ $withIcon?: boolean }>`
  color: ${({ theme }) => theme.palette.text.primary};
  overflow-wrap: anywhere;

  ${({ $withIcon, theme }) =>
    $withIcon &&
    css`
      display: flex;
      // The pin is one 24px line tall, so top-aligning keeps it on the first line of a wrapped address.
      align-items: flex-start;
      gap: ${theme.spacing(0.5)};
    `}

  & > p {
    font-size: 18px;
    line-height: 24px;
    letter-spacing: 0.4px;
    color: ${({ theme }) => theme.palette.text.primary};
  }

  // By reference, not p: the import puts LocationText's rules first, and jsdom 16 resolves by order, not specificity.
  & > ${LocationText} {
    margin: 0;
    font-weight: 400;
  }
`;

// A span, not a p: AnswerWrapper's `& > p` would outrank this class and paint it as a real answer.
export const NotAnswered = styled.span`
  display: block;
  font-size: 18px;
  line-height: 150%;
  letter-spacing: 0.15px;
  color: ${({ theme }) => theme.palette.text.disabled};
  user-select: none;
`;

export const NotApplicable = styled.span`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.palette.text.disabled};
`;
